"use client";

import { motion, useReducedMotion } from "framer-motion";
import Image from "next/image";
import { useState } from "react";
import { handleCardKeyDown, openProfile } from "../_logic/card-actions";
import { CARD_FOCUS_RING } from "../_logic/constants";
import type { TeamMember } from "../_logic/team-members";
import { TeamCardContent } from "./TeamCardContent";

/**
 * Desktop team card: a tilted "hanging" card that links to the member's profile.
 * @param props.member - The member to display.
 * @param props.index - Global position, used to stagger the entrance animation.
 * @param props.positionInRow - Index within its row, used for the arch rotation/offset.
 * @param props.rowCardCount - Number of cards in the row, used for the arch geometry.
 * @param props.centerRotationDirection - Tilt direction for a centered card.
 * @returns The rendered desktop card.
 */
export const HangingTeamCard = ({
  member,
  index,
  positionInRow,
  rowCardCount,
  centerRotationDirection = "right",
}: {
  member: TeamMember;
  index: number;
  positionInRow: number;
  rowCardCount: number;
  centerRotationDirection?: "left" | "right";
}) => {
  // Hooks
  const shouldReduceMotion = useReducedMotion();

  // States
  const [isHovered, setIsHovered] = useState(false);

  // Derived values
  // Arch rotation based on position in row
  const getArchRotation = () => {
    if (rowCardCount === 1) {
      // Single card centered
      return centerRotationDirection === "right" ? 3 : -3;
    } else if (rowCardCount === 2) {
      // Two cards: left rotates right (inward), right rotates left (inward)
      return positionInRow === 0 ? 8 : -6;
    } else {
      // Three or more cards: use standard 3-column logic
      const pos = positionInRow % 3;
      switch (pos) {
        case 0: // Left card
          return 8;
        case 1: // Center card
          return centerRotationDirection === "right" ? 3 : -3;
        case 2: // Right card
          return -6;
        default:
          return 0;
      }
    }
  };

  // Vertical offset for natural arch (center cards hang lower)
  const getArchOffset = () => {
    if (rowCardCount === 1) {
      return 40; // Single card hangs lower
    } else if (rowCardCount === 2) {
      return 40; // Both cards hang lower
    } else {
      // For 3+ cards, only center card hangs lower
      return positionInRow % 3 === 1 ? 40 : 0;
    }
  };

  const initialRotation = getArchRotation();
  const archOffset = getArchOffset();

  // Render
  return (
    <motion.div
      className="relative flex flex-col items-center"
      initial={shouldReduceMotion ? false : { opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        shouldReduceMotion
          ? { duration: 0 }
          : { duration: 0.6, delay: Math.min(index, 6) * 0.08, type: "spring", stiffness: 100, damping: 15 }
      }
      style={{ marginTop: archOffset }}
    >
      {/* Card */}
      <motion.div
        className={`bg-white rounded-3xl p-3 pb-5 flex flex-col gap-4 max-w-72 w-full shadow-sm relative cursor-pointer ${CARD_FOCUS_RING}`}
        style={{
          transformOrigin: "center top",
        }}
        initial={{
          rotate: initialRotation,
          transformOrigin: "center top",
        }}
        animate={{
          rotate: shouldReduceMotion ? initialRotation : isHovered ? initialRotation * 1.5 : initialRotation,
          y: shouldReduceMotion ? 0 : isHovered ? -8 : 0,
          transformOrigin: "center top",
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 20,
          duration: 0.3,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => openProfile(member.linkedin)}
        onKeyDown={(e) => handleCardKeyDown(e, member.linkedin)}
        role="button"
        tabIndex={0}
        aria-label={`View ${member.name}'s LinkedIn profile`}
        whileHover={shouldReduceMotion ? undefined : { scale: 1.02 }}
      >
        {/* Hanging hole at top center - outside card */}
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-8 h-6 bg-white rounded-t-full flex items-center justify-center">
            <div className="w-3 h-3 bg-gray-200 rounded-full"></div>
          </div>
        </div>

        {/* Profile Image */}
        <div className="relative w-full h-80 rounded-2xl bg-gray-200 overflow-hidden">
          <Image src={member.image} alt={member.name} fill sizes="288px" className="object-cover" />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-1.5">
          <TeamCardContent member={member} />
        </div>
      </motion.div>
    </motion.div>
  );
};
